import { CognitoIdentityProvider } from '@aws-sdk/client-cognito-identity-provider';
import { Alert, Button, Form, Input } from 'antd';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import get from 'lodash/get';
import { Component, createRef } from 'react';
import { withApollo } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { peekRedirectURL } from 'src/_shared/services/redirectAfterLogin.js';
import {
	GetLoginUser,
	updateUser,
} from 'src/_shared/api/graphql/custom/users/';
import { getEnvironment, lambda } from 'src/_shared/services/utils.js';
import {
	beginShellHandoff,
	getShellPortalUrl,
	isMobileShell,
	rememberShellHome,
	withShellParams,
} from 'src/_shared/shell.js';
import { resolveShellSignIn } from 'src/_shared/services/shellSignIn.js';
import { isSiteDisabledForUser } from 'src/_shared/services/disableSite.js';
import ClaimAccountModalForm from '../claim-account-modal/ClaimAccountModalComponent.jsx';
import ResetPasswordModal from '../reset-password-modal/ResetPasswordModal.jsx';

const handleAuthType = (type, reload = true) => {
	const authType = localStorage.getItem('authType');
	if (authType !== type) {
		localStorage.setItem('authType', type);
		if (reload) window.location.reload();
	}
};
const parse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

class LoginCard extends Component {
	constructor(props) {
		super(props);
		this.formRef = createRef();
		this.state = {
			// Mobile shell (ERIN-8561): one box for an email or phone number
			// first; the password field appears only for an enterprise account.
			shellStage: isMobileShell() ? 'identifier' : 'password',
			shellIdentifier: '',
			shellError: null,
			shellLoading: false,
			invalidCredentials: false,
			serverError: false,
			inactiveError: false,
			loading: false,
			visible: this.props.visible,
			resetModalVisible: false,
			inactiveLicense: false,
		};
	}

	async componentDidMount() {
		if (this.props.locationState) {
			const { currentUser, redirectURL } = this.props.locationState;
			const { setCurrentUser, onAuthentication, company } = this.props;
			// Resolve before onAuthentication: once auth flips, the /login route
			// redirects and consumes the stored destination. (ERIN-8413)
			const destination = redirectURL || peekRedirectURL('/dashboard');
			if (currentUser) {
				try {
					const { data } = await this.props.client.query({
						query: GetLoginUser,
						variables: {
							userId: currentUser?.id,
						},
					});

					const loginUser = data?.getLoginUser;
					if (isSiteDisabledForUser(loginUser)) {
						this.setState({ inactiveLicense: true });
						return;
					}

					const newUser = {
						...loginUser,
						lastLogin: dayjs().toISOString(),
					};
					const token = Cache.getItem('federatedToken')
						? Cache.getItem('federatedToken')?.id_token
						: Cookies.get('jwt');
					setCurrentUser(newUser);
					onAuthentication(token, newUser);
					this.props.client
						.mutate({
							mutation: gql(updateUser),
							variables: {
								input: {
									id: currentUser.id,
									lastLogin: dayjs().toISOString(),
								},
							},
						})
						.then(() => this.props.history.push(destination));
				} catch (error) {
					console.error(error);
				}
			}
		}
	}

	closeModal = () => {
		this.setState({ visible: false });
	};

	closeResetModal = () => {
		this.setState({ resetModalVisible: false, loading: false });
	};

	handleCompanyUserAccount = () => {
		const { company } = this.props;
		this.props.history.push(`/new-user-signup/${get(company, 'id')}`);
	};

	handleExternalUserAccount = () => {
		this.props.history.push({
			pathname: '/external-user',
			state: { company: this.props.company },
		});
	};

	handleLoginWithCompanyAccount = () => {
		const { openCompanyAccountModal, saml } = this.props;
		const samlPath = get(saml, 'path');
		if (samlPath) {
			this.props.history.push(samlPath);
		} else {
			openCompanyAccountModal();
		}
	};

	// Identifier-first sign-in in the mobile shell: see shellSignIn.js.
	handleShellContinue = async () => {
		const identifier = this.state.shellIdentifier;
		this.setState({ shellLoading: true, shellError: null });
		const result = await resolveShellSignIn({
			identifier,
			// Straight to lambda(): the graphql() helper swallows failures, and a
			// failed lookup must fall back to the password form, not read as
			// "no such user".
			lookupUser: (emailAddress) =>
				lambda(
					{
						endpoint: 'graphql',
						variables: {
							configMode: getEnvironment() === 'dev' ? 'DEV' : 'PROD',
							input: { emailAddress },
							query: 'getLoginUser',
						},
					},
					'apiKey'
				),
			defaultPortalUrl: getShellPortalUrl(),
		});
		switch (result.next) {
			case 'portal': {
				// Same path as the post-login hand-off (LogoutComponent) when the
				// person's company is known to have moved: remember the portal as
				// this install's home. A phone number or an unknown email is only
				// a guess, so the install is not pinned to the default portal.
				beginShellHandoff();
				if (result.companyId) {
					rememberShellHome({
						url: result.url,
						companyId: result.companyId,
						email: result.email,
					});
				}
				window.location.assign(
					withShellParams(result.url, {
						email: result.email,
						phone: result.phone,
					})
				);
				return;
			}

			case 'password': {
				this.formRef.current?.setFieldsValue({ email: result.email });
				this.props.setUserEmail(result.email);
				this.setState({ shellStage: 'password', shellLoading: false });
				return;
			}

			case 'unknown': {
				this.setState({
					shellLoading: false,
					shellError: 'We could not find an account for that email address.',
				});
				return;
			}

			default: {
				this.setState({
					shellLoading: false,
					shellError: 'Please enter a valid email address or phone number.',
				});
			}
		}
	};

	handleSubmit = async (values) => {
		this.setState({
			serverError: false,
			invalidCredentials: false,
			loading: true,
		});
		const { onAuthentication, setCurrentUser, setFailedLoginAttempts } =
			this.props;
		let currentUsr = '';
		try {
			const user = await Auth.signIn(
				values.email.toLowerCase().trim(),
				values.password.trim()
			);
			if (get(user, 'challengeName') === 'NEW_PASSWORD_REQUIRED') {
				this.setState({
					resetModalVisible: true,
					authUser: user,
					loading: false,
				});
				return;
			}

			const token = user?.signInUserSession?.idToken?.payload;
			const userId = token?.['custom:userId'];
			const { data } = await this.props.client.query({
				query: GetLoginUser,
				variables: {
					userId,
					token: JSON.stringify(token),
				},
			});
			const loginUser = data?.getLoginUser;

			const lastLoginValue = loginUser?.lastLogin;
			let value = Boolean(!lastLoginValue) + '';
			const resetPopup = loginUser?.company?.resetPopup;

			if (resetPopup) {
				value = 'true';
			}

			window.localStorage.setItem('lastLoginValue', value);
			window.localStorage.setItem('showFirstTimePopup', value);
			const currentUser = {
				...loginUser,
				authMethod: 'credentials',
				lastLogin: dayjs().toISOString(),
			};
			currentUsr = currentUser;

			if (isSiteDisabledForUser(currentUser)) {
				const error = new Error('License Invalid');
				error.code = 'UserLicenseInvalid';
				throw error;
			}

			if (currentUser && !currentUser.active) {
				const error = new Error('User Disabled');
				error.code = 'UserDisabledException';
				throw error;
			}

			setCurrentUser(currentUser);
			await this.props.client.mutate({
				mutation: gql(updateUser),
				variables: {
					input: {
						id: loginUser.id,
						authMethod: 'credentials',
						lastLogin: dayjs().toISOString(),
						accessToken: user.signInUserSession.accessToken.jwtToken,
						expires: false,
					},
				},
			});
			onAuthentication(
				user.signInUserSession.accessToken.jwtToken,
				currentUser
			);
			this.setState({ loading: false });
		} catch (error) {
			if (
				error.message ===
				'Network error: Response not successful: Received status code 401'
			) {
				window.location.reload();
				return;
			}

			if (error.message === 'Unauthorized: Account not active') {
				this.setState({ inactiveError: true, loading: false });
				return;
			}

			if (error.message === 'Unauthorized: Site disabled') {
				this.setState({ inactiveLicense: true, loading: false });
				return;
			}

			switch (error.code) {
				case 'NotAuthorizedException':
				case 'UserNotFoundException': {
					this.setState({ invalidCredentials: true });
					if (currentUsr) {
						setFailedLoginAttempts(currentUsr);
					}

					break;
				}

				case 'UserDisabledException': {
					this.setState({ inactiveError: true });
					break;
				}

				case 'NetworkError': {
					this.setState({ serverError: true });
					break;
				}

				case 'UserLicenseInvalid': {
					this.setState({ inactiveLicense: true });
					break;
				}

				default: {
					console.error('Authentication error:', error);
					this.setState({ serverError: true });
				}
			}

			this.setState({ loading: false });
		}
	};

	updateNewPassword = (user, newPassword) => {
		const parameters = {
			ChallengeName: 'NEW_PASSWORD_REQUIRED',
			ClientId: get(user, 'pool.clientId'),
			ChallengeResponses: {
				USERNAME: get(user, 'username'),
				NEW_PASSWORD: newPassword,
			},
			Session: get(user, 'Session'),
		};
		const config = {
			region: 'us-east-2',
		};
		const { closeResetModal } = this;
		const cognitoidentityserviceprovider = new CognitoIdentityProvider(config);
		cognitoidentityserviceprovider.respondToAuthChallenge(
			parameters,
			function (error, data) {
				if (error) console.log(error, error.stack);
				else {
					closeResetModal();
				}
			}
		);
	};

	renderError = (error, message) => {
		return error ? <Alert type="error" message={message} /> : null;
	};

	renderSuccess = (error, message) => {
		return error ? <Alert type="success" message={message} /> : null;
	};

	renderWarning = (error, message) => {
		return error ? <Alert type="warning" message={message} /> : null;
	};

	render() {
		const { shellStage } = this.state;
		const { serverError, invalidCredentials, inactiveError, inactiveLicense } =
			this.state;
		const {
			theme,
			accountClaimError,
			accountClaimSuccess,
			companyAdminSuccess,
			successMessage,
			warningMessage,
			externalUserSuccess,
			company,
			whiteLabel,
			openForgotPasswordModal,
			labelEmployeeID,
			loginEmailLabel,
			userSignupSettings,
		} = this.props;
		const loginFormSettings = parse(get(company, 'loginFormSettings'));

		return (
			<>
				<h2 className="auth-title text-center">
					Log in to {company && whiteLabel ? company?.name : 'ERIN'}
				</h2>
				<Form ref={this.formRef} onFinish={this.handleSubmit}>
					{shellStage === 'identifier' && (
						<div className="custom-form-group shell-identifier">
							<label className="custom-label" data-testid="login-identifier">
								Email or phone number
							</label>
							<Input
								autoComplete="username"
								inputMode="email"
								className="custom-input"
								value={this.state.shellIdentifier}
								onChange={(e) =>
									this.setState({
										shellIdentifier: e.target.value.replaceAll(/\s+/g, ''),
										shellError: null,
									})
								}
								onPressEnter={this.handleShellContinue}
							/>
							{this.renderError(this.state.shellError, this.state.shellError)}
							<Button
								block
								loading={this.state.shellLoading}
								htmlType="button"
								type="primary"
								size="large"
								onClick={this.handleShellContinue}
							>
								Continue
							</Button>
						</div>
					)}
					<div className="custom-form-group" hidden={shellStage !== 'password'}>
						<label className="custom-label" data-testid="login-email">
							{loginEmailLabel ?? 'Email Address'}
						</label>
						<Form.Item
							name="email"
							rules={[
								{
									type: 'email',
									message: 'Please enter a valid email address.',
								},
								{
									required: true,
									message: 'Email Required',
								},
							]}
							validateTrigger="onSubmit"
						>
							<Input
								autoComplete="off"
								className="custom-input"
								onInput={(e) => {
									e.target.value = e.target.value.replaceAll(/\s+/g, '');
									this.props.setUserEmail(e.target.value);
								}}
								onFocus={() => handleAuthType('default')}
							/>
						</Form.Item>
					</div>
					<div className="custom-form-group" hidden={shellStage !== 'password'}>
						<label className="custom-label">Password</label>
						<Form.Item
							name="password"
							rules={[
								{
									required: true,
									message: 'Password Required',
								},
							]}
						>
							<Input.Password
								className="custom-input"
								autoComplete="off"
								onFocus={() => handleAuthType('default')}
							/>
						</Form.Item>
					</div>
					{shellStage === 'password' && (
						<div className="forgot-link">
							<Button type="link" onClick={openForgotPasswordModal}>
								Forgot Your Password?
							</Button>
						</div>
					)}

					{this.renderError(
						serverError,
						'There was a problem with the server, please try again.'
					)}
					{this.renderError(
						invalidCredentials,
						'User/password combination not found. Please try again.'
					)}
					{this.renderError(
						inactiveError,
						'Your account has been disabled.  Please contact an adminstrator to reactivate your account.'
					)}
					{this.renderError(
						inactiveLicense,
						'Your license of ERIN is no longer valid. Please contact your company admin for more information.'
					)}
					{this.renderError(accountClaimError, accountClaimError)}
					{this.renderSuccess(accountClaimSuccess, accountClaimSuccess)}
					{this.renderSuccess(companyAdminSuccess, companyAdminSuccess)}
					{this.renderSuccess(successMessage, successMessage)}
					{this.renderWarning(warningMessage, warningMessage)}
					{this.renderSuccess(externalUserSuccess, externalUserSuccess)}
					{shellStage === 'password' && (
						<Button
							block
							loading={this.state.loading}
							htmlType="submit"
							type="primary"
							size="large"
						>
							Log In
						</Button>
					)}
					{!get(company, 'disableSAMLLogin', false) && (
						<>
							<div className="or-style">or</div>
							<Button
								ghost
								block
								type="primary"
								size="large"
								onClick={this.handleLoginWithCompanyAccount}
							>
								Log in with a Company Account
							</Button>
						</>
					)}
					{get(company, 'whiteLabel') &&
						get(company, 'externalUserSignUp') &&
						!get(loginFormSettings, 'companyUserSignup.enabled') && (
							<>
								<div className="or-style">or</div>
								<Button
									ghost
									block
									type="primary"
									size="large"
									onClick={this.handleExternalUserAccount}
								>
									Create An Account
								</Button>
							</>
						)}
					{get(company, 'whiteLabel') &&
						get(loginFormSettings, 'companyUserSignup.enabled') && (
							<>
								<div className="or-style">or</div>
								<Button
									ghost
									block
									type="primary"
									size="large"
									onClick={this.handleCompanyUserAccount}
								>
									Create An Account
								</Button>
							</>
						)}
					{!get(company, 'disableClaimYourAccountLogin') && (
						<div className="claim-your-account">
							<Button type="link" onClick={this.props.handleClaimAccount}>
								Claim Your Account
							</Button>
						</div>
					)}
				</Form>

				<p className="by-continuing-links">
					*By continuing you agree to the{' '}
					<a
						href="https://erinapp.com/terms-of-use/"
						target="_blank"
						rel="noopener noreferrer"
					>
						User Agreement
					</a>
					{', '}
					<a
						href="https://erinapp.com/privacy-policy/"
						target="_blank"
						rel="noopener noreferrer"
					>
						Privacy Policy
					</a>
					, and{' '}
					<a
						href="https://erinapp.com/cookie-policy/"
						target="_blank"
						rel="noopener noreferrer"
					>
						Cookie Policy
					</a>{' '}
					for this application.
				</p>

				<ClaimAccountModalForm
					props={this.props}
					theme={theme}
					visible={this.props.visible}
					page={1}
					setPageOne={this.setPageOne}
					closeModal={this.props.closeModal}
					labelEmployeeID={labelEmployeeID}
					userSignupSettings={userSignupSettings}
				/>
				<ResetPasswordModal
					theme={theme}
					authUser={this.state.authUser}
					visible={this.state.resetModalVisible}
					closeModal={this.closeResetModal}
					updateNewPassword={this.updateNewPassword}
				/>
			</>
		);
	}
}

export default withApollo(withRouter(LoginCard));
